/**
 * Blue Light landing page.
 *
 * Three behaviours, all scoped to a `.blp-page` section:
 *
 * 1. Carousel — an endless three-up carousel. Rather than cloning slides, each
 *    slide is given its signed distance to the active index (wrapped into
 *    [-n/2, n/2]); only -1, 0 and 1 are visible, so the loop never hits an edge
 *    and there is nothing to reset. CSS owns the geometry.
 * 2. Video — retries autoplay where the browser refused it.
 * 3. Add to cart — matches the theme's existing behaviour: POST to
 *    /cart/add.js, then go to the cart.
 */
(function () {
  var BREAKPOINT = '(min-width: 768px)';
  var SWIPE_THRESHOLD = 40;

  function shopifyRoot() {
    return window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root
      : '/';
  }

  /* --- carousel ---------------------------------------------------------- */

  function Carousel(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(
      root.querySelectorAll('.blp-carousel__slide')
    );
    this.count = this.slides.length;
    this.index = 0;
    this.timer = null;
    this.autoplay = root.dataset.autoplay === 'true';
    this.interval = parseInt(root.dataset.interval, 10) || 4000;

    if (this.count === 0) return;

    this.render();
    this.bind();
    this.observe();
  }

  /**
   * Signed distance from the active slide, wrapped so that the last slide is
   * "one before" the first. Returns null for slides that are out of view.
   */
  Carousel.prototype.offset = function (i) {
    var n = this.count;
    var d = (((i - this.index) % n) + n) % n;
    if (d > n / 2) d -= n;
    return d >= -1 && d <= 1 ? d : null;
  };

  Carousel.prototype.render = function () {
    for (var i = 0; i < this.count; i++) {
      var d = this.offset(i);
      var slide = this.slides[i];
      if (d === null) {
        slide.removeAttribute('data-pos');
      } else {
        slide.setAttribute('data-pos', String(d));
      }
      // Keep the off-screen cards out of the tab order and off the a11y tree.
      slide.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
      var focusable = slide.querySelectorAll('a, button');
      for (var j = 0; j < focusable.length; j++) {
        focusable[j].tabIndex = d === 0 ? 0 : -1;
      }
    }
  };

  Carousel.prototype.go = function (delta) {
    if (this.count < 2) return;
    this.index = (((this.index + delta) % this.count) + this.count) % this.count;
    this.render();
  };

  Carousel.prototype.start = function () {
    if (!this.autoplay || this.count < 2 || this.timer) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var self = this;
    this.timer = setInterval(function () {
      self.go(1);
    }, this.interval);
  };

  Carousel.prototype.stop = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  Carousel.prototype.bind = function () {
    var self = this;
    var startX = null;

    this.root.addEventListener('pointerdown', function (event) {
      startX = event.clientX;
      self.stop();
    });

    this.root.addEventListener('pointerup', function (event) {
      if (startX === null) return;
      var dx = event.clientX - startX;
      startX = null;
      if (Math.abs(dx) > SWIPE_THRESHOLD) {
        self.go(dx < 0 ? 1 : -1);
      }
      self.start();
    });

    this.root.addEventListener('pointercancel', function () {
      startX = null;
      self.start();
    });

    // A tap on a neighbour brings it to the front rather than following its
    // link — the link belongs to the card that is already in focus.
    this.slides.forEach(function (slide, i) {
      slide.addEventListener(
        'click',
        function (event) {
          var d = self.offset(i);
          if (d !== 0 && d !== null) {
            event.preventDefault();
            event.stopPropagation();
            self.go(d);
          }
        },
        true
      );
    });

    this.root.addEventListener('mouseenter', function () {
      self.stop();
    });

    this.root.addEventListener('mouseleave', function () {
      self.start();
    });

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.go(-1);
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.stop();
      } else {
        self.start();
      }
    });
  };

  Carousel.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.start();
      return;
    }
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.start();
          } else {
            self.stop();
          }
        });
      },
      { threshold: 0.2 }
    ).observe(this.root);
  };

  /* --- headline fitting --------------------------------------------------- */

  /**
   * The artboard was set in Helvetica Neue Condensed Black — a macOS system
   * face that cannot be served on the web. The licensed Heavy Condensed the
   * theme ships is a touch wider, so a couple of the longest lines would run
   * past the measure the designer drew. Shrink only those, by exactly the
   * amount needed.
   *
   * The ratio is resolution-independent (text and measure both scale with the
   * viewport), so it only has to be recomputed when the breakpoint flips or
   * the webfont finishes loading.
   */
  function fitHeadlines(page) {
    var heads = page.querySelectorAll('.blp-head, .blp-hero__title');
    var fits = [];
    for (var i = 0; i < heads.length; i++) {
      var head = heads[i];
      head.style.removeProperty('--blp-fit');
      fits[i] = 1;

      var measure = head.clientWidth;
      if (!measure) continue;

      var lines = head.querySelectorAll(
        '.blp-head__line, .blp-hero__line, .blp-hero__word'
      );
      var widest = 0;
      for (var j = 0; j < lines.length; j++) {
        // Only the breakpoint's own set of lines has a layout box.
        if (!lines[j].getClientRects().length) continue;
        if (lines[j].scrollWidth > widest) widest = lines[j].scrollWidth;
      }

      if (widest > measure) fits[i] = measure / widest;
    }

    // Headlines stacked in the same section (above and below an image or
    // video) read as one statement, so they all take the smallest fit.
    for (var a = 0; a < heads.length; a++) {
      var fit = fits[a];
      for (var b = 0; b < heads.length; b++) {
        if (heads[b].parentElement === heads[a].parentElement) {
          fit = Math.min(fit, fits[b]);
        }
      }
      if (fit < 1) heads[a].style.setProperty('--blp-fit', fit.toFixed(4));
    }
  }

  function watchHeadlines(page) {
    var query = window.matchMedia(BREAKPOINT);
    var run = function () {
      fitHeadlines(page);
    };

    run();

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', run);
    } else if (typeof query.addListener === 'function') {
      query.addListener(run);
    }

    // Measuring before the webfont swaps in would size against the fallback.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run);
    }
  }

  /* --- video autoplay fallback ------------------------------------------- */

  /**
   * Muted inline autoplay is still refused in some cases (iOS Low Power Mode,
   * data savers), which leaves the poster frozen. Retry when the video scrolls
   * into view, and on every tap until it plays — playback started by a user
   * gesture is always allowed. While the browser keeps refusing, a play button
   * tells the visitor a tap is needed.
   */
  function setupVideo(video) {
    var button = video.parentElement.querySelector('.blp-video__play');

    function tryPlay() {
      if (!video.paused) return;
      video.muted = true;
      var playing = video.play();
      if (playing && typeof playing.catch === 'function') {
        playing.catch(function (error) {
          if (button && error && error.name === 'NotAllowedError') {
            button.hidden = false;
          }
        });
      }
    }

    if (button) button.addEventListener('click', tryPlay);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) tryPlay();
      }).observe(video);
    }

    document.addEventListener('touchend', tryPlay, { passive: true });
    document.addEventListener('click', tryPlay);
    video.addEventListener('playing', function () {
      if (button) button.hidden = true;
      document.removeEventListener('touchend', tryPlay);
      document.removeEventListener('click', tryPlay);
    });
  }

  /* --- add to cart ------------------------------------------------------- */

  function setupCartButton(button) {
    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      var variantId = button.dataset.variantId;
      if (!variantId || button.getAttribute('aria-busy') === 'true') return;

      button.setAttribute('aria-busy', 'true');

      fetch(shopifyRoot() + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart/add.js ' + response.status);
          return response.json();
        })
        .then(function () {
          window.location.href = shopifyRoot() + 'cart';
        })
        .catch(function () {
          button.removeAttribute('aria-busy');
          // Fall back to the product page so the shopper still has a way
          // through when the cart rejects the add (sold out, limits).
          var href = button.dataset.productUrl;
          if (href) window.location.href = href;
        });
    });
  }

  /* --- boot -------------------------------------------------------------- */

  function init(scope) {
    var pages = (scope || document).querySelectorAll('.blp-page');
    for (var i = 0; i < pages.length; i++) {
      var page = pages[i];
      if (page.dataset.blpReady === 'true') continue;
      page.dataset.blpReady = 'true';

      watchHeadlines(page);

      var carousel = page.querySelector('.blp-carousel');
      if (carousel) new Carousel(carousel);

      var videos = page.querySelectorAll('.blp-video');
      for (var v = 0; v < videos.length; v++) setupVideo(videos[v]);

      var buttons = page.querySelectorAll('.blp-card__cart');
      for (var b = 0; b < buttons.length; b++) setupCartButton(buttons[b]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders a section in place; re-init the new markup.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
